import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";

import { StandardTableActions } from "../../../../../../../components/StandardTableActions/StandardTableActions";
import { AddOutlined } from "@mui/icons-material";
import produce from "immer";
import { ConfirmDialog } from "../../../../../../../components/Dialogs/ConfirmDialog";
import { IAutomobileAdditionalInsured, IAutomobileCustomerInfo } from "../../../../../../../redux/automobileBasicDetails/automobileBasicDetails.types";
import { AdditionalInsuredForm } from "../AdditionalInsured/AdditionalInsuredDialog/AdditionalInsuredDialog";

interface ICustomerContactTableProps {
  values: IAutomobileCustomerInfo;
  customerPolicyId?: number;
  customerId?: number;
  setValues: (newValues: IAutomobileCustomerInfo) => void;
}

const INITIAL_STATE: IAutomobileAdditionalInsured = {
  customer_policy_id: 0,
  incremental_number: null,
  customer_id: 0,
  type: null,
  customer_name: null,
  address: null,
  unit_or_suite: null,
  city: null,
  province_or_state: null,
  country: null,
  postal_code: null,
  phone_number: null,
  mobile_number: null,
  gender: null,
  birth_date: null,
  marital_status: null,
  status: "ACTIVE",
};

export const CustomerContactTable: React.FC<ICustomerContactTableProps> = ({
  values,
  customerPolicyId,
  customerId,
  setValues,
}) => {
  const [openContact, setOpenContact] =
    React.useState<IAutomobileAdditionalInsured | null>(null);
  const [openConfirm, setOpenConfirm] =
    React.useState<IAutomobileAdditionalInsured | null>(null);

  const handleComplete = (newData: IAutomobileAdditionalInsured) => {
    const newValues = produce(values, (draftValues) => {
      if (newData.incremental_number) {
        const index = values.additional_insured.findIndex(
          (x) => x.incremental_number === newData.incremental_number
        );
        if (index > -1) {
          draftValues.additional_insured[index] = newData;
        }
      } else {
        draftValues.additional_insured.push(newData);
      }
    });
    setValues(newValues);
    setOpenContact(null);
  };

  const handleConfirm = () => {
    const newValues = produce(values, (draftValues) => {
      if (openConfirm && openConfirm.incremental_number) {
        const index = values.additional_insured.findIndex(
          (x) => x.incremental_number === openConfirm.incremental_number
        );
        if (index > -1) {
          draftValues.additional_insured[index].status = "INACTIVE";
        }
      }
    });
    setValues(newValues);
    setOpenConfirm(null);
    setOpenContact(null);
  };

  return (
    <>
      <Stack direction={"row"} justifyContent={"end"}>
        <Button
          variant="contained"
          onClick={() => {
            if (customerId) {
              setOpenContact({
                ...INITIAL_STATE,
                customer_id: customerId,
                customer_policy_id: customerPolicyId || null,
              });
            }
          }}
          startIcon={<AddOutlined />}
        >
          Create
        </Button>
      </Stack>
      <TableContainer sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Actions</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Customer Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Date of Birth</TableCell>
              <TableCell>Marital Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(values.additional_insured || []).filter(x=>x.status === "ACTIVE").map((item, index) => {
              return (
                <TableRow key={index}>
                  <TableCell>
                    <StandardTableActions
                      onEditClick={() => {
                        setOpenContact(item);
                      }}
                      onDeleteClick={() => {
                        setOpenConfirm(item);
                      }}
                    />
                  </TableCell>
                  <TableCell>{item.type}</TableCell>
                  <TableCell>{item.customer_name}</TableCell>
                  <TableCell>{item.mobile_number}</TableCell>
                  <TableCell>{item.phone_number}</TableCell>
                  <TableCell>{item.gender}</TableCell>
                  <TableCell>{item.birth_date}</TableCell>
                  <TableCell>{item.marital_status}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {openContact && (
        <AdditionalInsuredForm
          open={true}
          insured={openContact}
          onClose={() => setOpenContact(null)}
          onComplete={handleComplete}
        />
      )}

      {openConfirm && (
        <ConfirmDialog
          open={true}
          content="Are you sure you want to delete this record?"
          onConfrim={handleConfirm}
          onClose={() => setOpenConfirm(null)}
        />
      )}
    </>
  );
};
